import { GridArea } from '../types/grid';
import { Position } from '../Utils';

/**
 * Validates that a GridArea configuration is within bounds and properly formatted
 */
export function validateGridArea(gridArea: GridArea): void {
    const { row, col, rows, cols, gridSize = 5 } = gridArea;
    
    // Validate minimum values (1-indexed system)
    if (row < 1 || col < 1) {
        throw new Error(
            `GridArea uses 1-indexed coordinates. Got row: ${row}, col: ${col}. ` +
            `Minimum values are row: 1, col: 1.`
        );
    }
    
    // Validate grid bounds
    if (row + rows - 1 > gridSize) {
        throw new Error(
            `GridArea exceeds vertical bounds. ` +
            `Row ${row} with ${rows} rows exceeds grid size ${gridSize}. ` +
            `Max row for this span: ${gridSize - rows + 1}`
        );
    }
    
    if (col + cols - 1 > gridSize) {
        throw new Error(
            `GridArea exceeds horizontal bounds. ` +
            `Column ${col} with ${cols} columns exceeds grid size ${gridSize}. ` +
            `Max column for this span: ${gridSize - cols + 1}`
        );
    }
    
    // Validate positive spans
    if (rows < 1 || cols < 1) {
        throw new Error(
            `GridArea spans must be positive. Got rows: ${rows}, cols: ${cols}`
        );
    }
    
    // Validate grid size
    if (gridSize < 1) {
        throw new Error(
            `Grid size must be positive. Got gridSize: ${gridSize}`
        );
    }
}

/**
 * Validates that position and gridArea are not used together
 */
export function validatePositionGridAreaCombination(
    position?: Position,
    gridArea?: GridArea,
    itemId?: string
): void {
    // Check for conflicting usage
    if (position && gridArea) {
        throw new Error(
            `Item '${itemId || 'unknown'}' cannot use both 'position' and 'gridArea'. ` +
            `Choose one positioning method:\n` +
            `- position: ${position} (for semantic positioning)\n` +
            `- gridArea: row ${gridArea.row}, col ${gridArea.col} (for precise control)\n` +
            `Current config will cause unpredictable positioning.`
        );
    }
    
    // Require at least one positioning method
    if (!position && !gridArea) {
        console.warn(
            `Item '${itemId || 'unknown'}' has no positioning specified. ` +
            `Using default center position. Consider specifying either 'position' or 'gridArea'.`
        );
    }
}

/**
 * Detects legacy usage patterns that are no longer supported
 */
export function detectLegacyUsage(item: any): void {
    // Detect old patterns that will break
    if (item.gridArea?.row === 0 || item.gridArea?.col === 0) {
        throw new Error(
            `BREAKING CHANGE: 0-indexed grid coordinates no longer supported.\n` +
            `Item '${item.id}' uses row: ${item.gridArea.row}, col: ${item.gridArea.col}\n` +
            `Solution: Change to 1-indexed (row: 0 → row: 1, col: 0 → col: 1)\n` +
            `This change prevents negative positioning bugs.`
        );
    }
    
    // Detect position + gridArea combination (now an error)
    if (item.position && item.gridArea) {
        const suggestedFix = item.position === Position.Center 
            ? `Remove 'position' property (gridArea will center content by default)`
            : `Remove 'gridArea' property (position will auto-map to appropriate grid area)`;
            
        throw new Error(
            `BREAKING CHANGE: Cannot use both position and gridArea together.\n` +
            `Item '${item.id}' has both position: ${item.position} and gridArea specified.\n` +
            `Solution: ${suggestedFix}`
        );
    }
}

/**
 * Validates semantic consistency between position name and grid location
 * This logs warnings but doesn't throw errors
 */
export function validateSemanticPosition(position: Position, gridArea: GridArea): void {
    const warnings: string[] = [];
    const { row, col, gridSize = 5 } = gridArea;
    
    // Calculate grid midpoints
    const midRow = Math.ceil(gridSize / 2);
    const midCol = Math.ceil(gridSize / 2);
    
    // Check top positions
    if (position.toString().includes('Top') && row > midRow) {
        warnings.push(`Position '${position}' implies top area, but gridArea row ${row} is in bottom half`);
    }
    
    // Check bottom positions  
    if (position.toString().includes('Bottom') && row < midRow) {
        warnings.push(`Position '${position}' implies bottom area, but gridArea row ${row} is in top half`);
    }
    
    // Check left positions
    if (position.toString().includes('Left') && col > midCol) {
        warnings.push(`Position '${position}' implies left area, but gridArea col ${col} is in right half`);
    }
    
    // Check right positions
    if (position.toString().includes('Right') && col < midCol) {
        warnings.push(`Position '${position}' implies right area, but gridArea col ${col} is in left half`);
    }
    
    if (warnings.length > 0) {
        console.warn(
            `Semantic position mismatch detected:\n` +
            warnings.join('\n') +
            `\nThis may cause unexpected visual results.`
        );
    }
}